import { Component, computed, effect, OnInit, signal } from '@angular/core';
import { SitesShortForm } from '../../interfaces/regions';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SiteLockCodesService } from '../../services/site-lock-codes.services';
import { LookUpService } from '../../services/lookupservice';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { SaveSiteLockCodes, SiteLockCodesResponse } from '../../interfaces/site-lock-codes-response';
import { NotificationService } from '../../services/notificationservice';
import { MatButtonModule } from '@angular/material/button';
import { LoadScheduleControlFlags, ScreenName } from '../../interfaces/securityPermissions';
import { RoleService } from '../../services/role.service';
import { RolePermissionsService } from '../../services/role-permissions.service';

@Component({
  selector: 'app-sitelockcodes',
  imports: [CommonModule, MatInputModule, MatSelectModule,
    MatFormFieldModule, ReactiveFormsModule, MatIconModule, MatIconModule,
    MatButtonModule
  ],
  templateUrl: './sitelockcodes.component.html',
  styleUrl: './sitelockcodes.component.scss',


})
export class SiteLockCodesComponent implements OnInit {

  //Form
  form!: FormGroup;
  //Signals
  sites = signal<SitesShortForm[]>([]);
  selectedSiteId = signal<number>(0);
  siteSearchQuery = signal<string>('');
  displayValue = signal<string>('');

  private destroy$ = new Subject<void>();
  controlPermissions: LoadScheduleControlFlags = new LoadScheduleControlFlags();
  //Constants
  readonly MARKET_ID = -1;
  readonly SCHEDULE_TYPE_ID = 1;

  constructor(private _fb: FormBuilder,
    private siteLockCodeSvc: SiteLockCodesService,
    private lookupSvc: LookUpService,
    private _notificationSvc: NotificationService,
    private rolesService: RoleService,
    private rpService: RolePermissionsService) {

    //When market changes: reset site, load sites, and clear pending
    effect(() => {
      const siteId = this.selectedSiteId();
      this.getSiteLockCodesById(siteId);
    });
  }

  ngOnInit(): void {

    this.form = this._fb.group({
      siteId: [this.selectedSiteId(), Validators.required],
      siteLockCodes: this._fb.array([])
    })

    this.getDisposalSites();
  }

  getDisposalSites() {
    let roles = this.rolesService.getRolesAsString();
    this.lookupSvc.getSitesByMarketId(this.MARKET_ID, this.SCHEDULE_TYPE_ID).pipe(takeUntil(this.destroy$)).subscribe({
      next: (sites: SitesShortForm[]) => {
        this.sites.set(sites);
      }
    })
    this.rpService.getRolePermissionsNew(roles, ScreenName.SLC, 0).subscribe({
      next: (perms: LoadScheduleControlFlags) => {
        this.controlPermissions = perms;
      }
    })
  }

  // --- select handlers
  onSiteSelected(value: number): void {
    this.selectedSiteId.set(value);
  }
  onSiteSearchQuery(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value.toLowerCase();
    this.siteSearchQuery.set(searchTerm);
  }
  sitesFiltered = computed(() => {
    const q = this.siteSearchQuery().trim().toLowerCase();
    return this.sites().filter(m => m.siteName.toLowerCase().includes(q));
  });

  getSiteLockCodesById(siteId: number) {
    this.siteLockCodeSvc.getSiteLockCodesById(siteId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: SiteLockCodesResponse[]) => {

        this.siteLockCodesFormArray.clear();

        if (response != null && response.length > 0) {
          response.forEach(f => {
            this.siteLockCodesFormArray.push(
              this._fb.group({
                tankId: [f.tankID, Validators.required],
                tankName: [f.tankName],
                colorCode: [f.colorCode || '#e9ecef'],
                siteLockCode: [f.siteLockCode, Validators.required]
              })
            )
          });
        }
      }
    })
  }

  roundOff(event: Event, maxValue: number, maxDecimals: number, type: number): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;

    if (maxDecimals === 0) {
      val = val.replace(/[^0-9]/g, '');
    } else {
      val = val.replace(/[^0-9.]/g, '');
    }
    const points = val.split('.');
    if (points.length > 2) {
      val = points[0] + '.' + points.slice(1).join('');
    }
    
    const numericValue = parseFloat(val);
    if (!isNaN(numericValue) && numericValue > maxValue) {
      val = maxValue.toString();
    }
    
    if (val.includes('.')) {
      const [intPart, decimalPart] = val.split('.');
      if (decimalPart.length > maxDecimals) {
        val = `${intPart}.${decimalPart.substring(0, maxDecimals)}`;
      }
    }
    this.displayValue.set(val);
    
    input.value = val;
    const isValueEmpty = val === '' || val === '.';
    const parsedValue = maxDecimals === 0 ? parseInt(val, 10) : parseFloat(val);
    const finalValue = isValueEmpty ? null : parsedValue;

    requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.value = val;   // put "14.10" back so next keystroke appends correctly
      }
    });
  }


  get siteLockCodesFormArray() {
    return this.form.get('siteLockCodes') as FormArray;
  }

  onSubmit() {

    if (!this.form || this.form.invalid) {
      this.form?.markAllAsTouched();
      return;
    }

    var payload: SaveSiteLockCodes = {
      siteId: this.form.value.siteId,
      siteLockCodes: this.siteLockCodesFormArray.controls.map(fg => ({
        tankId: fg.get('tankId')?.value,
        siteLockCode: fg.get('siteLockCode')?.value
      }))
    }

    this.siteLockCodeSvc.saveSiteLockCodes(payload).pipe(takeUntil(this.destroy$)).subscribe({
      next: (isSuccess: boolean) => {
        if (isSuccess) {
          this._notificationSvc.show('Site Lock Codes saved successfully', 'success');
        }
      }
    })
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

